import { useEffect, useState } from "react";
import api from "../services/api";
import { Gift, Shield, Trophy, Loader2, IndianRupee, CheckCircle2, Clock, AlertTriangle } from "lucide-react";

export default function AgentPerksCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/agent/welcome-kit/me");
        if (alive) setData(data);
      } catch { /* ignore — agent may not be verified yet */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-slate-100 p-6 animate-fadeInUp" data-testid="agent-perks-loading">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400 mx-auto" />
      </div>
    );
  }

  if (!data) return null;

  const kit = data.welcome_kit || {};
  const ins = data.insurance || {};
  const bonus = data.joining_bonus || {};

  const progressPct = Math.min(100, Math.round(((bonus.pickups_completed || 0) / Math.max(1, bonus.required_pickups || 1)) * 100));

  const retentionPct = kit.retention_days > 0
    ? Math.min(100, Math.round(((kit.retention_days - (kit.days_remaining ?? kit.retention_days)) / kit.retention_days) * 100))
    : 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 animate-fadeInUp" data-testid="agent-perks-card">
      {/* WELCOME KIT */}
      <div className="bg-gradient-to-br from-orange-50 via-amber-50/60 to-white border border-orange-200/80 rounded-2xl p-5 lg:col-span-1" data-testid="perks-welcome-kit">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 rounded-xl bg-[#FF6B00] text-white flex items-center justify-center shadow-sm shadow-[#FF6B00]/30">
            <Gift className="h-4.5 w-4.5" />
          </div>
          <div className="flex-1">
            <div className="font-['Outfit'] font-bold text-slate-900 text-sm">Welcome Kit</div>
            <div className="text-[11px] text-slate-500">Issued on day one · worth <span className="font-bold text-[#FF6B00]">₹{kit.total_value || 0}</span></div>
          </div>
          {kit.status === "retained" && (
            <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded" data-testid="kit-retained-badge">
              <CheckCircle2 className="h-2.5 w-2.5" /> YOURS
            </span>
          )}
          {kit.status === "returned" && (
            <span className="text-[9px] font-bold text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded">RETURNED</span>
          )}
          {kit.status === "recovered" && (
            <span className="text-[9px] font-bold text-red-600 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded">RECOVERED</span>
          )}
        </div>

        <div className="grid grid-cols-3 gap-1.5 mb-3">
          {(kit.items || []).map((it) => (
            <div key={it.id} className="bg-white/80 border border-slate-100 rounded-lg p-2 text-center" data-testid={`kit-item-${it.id}`}>
              <div className="text-xl">{it.emoji}</div>
              <div className="text-[9px] font-semibold text-slate-700 mt-0.5 leading-tight line-clamp-2">{it.name}</div>
              <div className="text-[9px] text-slate-400">₹{it.value}</div>
            </div>
          ))}
        </div>

        {kit.status !== "retained" && kit.status !== "returned" && kit.status !== "recovered" && (
          <>
            <div className="flex items-center justify-between text-[10px] font-semibold text-slate-600 mb-1">
              <span>Retention progress</span>
              <span>{kit.days_remaining ?? kit.retention_days} days left</span>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden" data-testid="kit-retention-bar">
              <div className="h-full bg-[#FF6B00] transition-all" style={{ width: `${retentionPct}%` }} />
            </div>
            <div className="mt-2.5 rounded-lg bg-amber-50 border border-amber-200 px-2.5 py-1.5 text-[10px] text-amber-800 flex items-start gap-1.5" data-testid="kit-return-notice">
              <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
              <span>Leave before {kit.retention_days} days of active work and you must return the kit or pay back <span className="font-bold">₹{kit.total_value}</span>.</span>
            </div>
          </>
        )}
      </div>

      {/* LIFE / ACCIDENT INSURANCE */}
      <div className="bg-gradient-to-br from-emerald-50 via-teal-50/60 to-white border border-emerald-200/80 rounded-2xl p-5" data-testid="perks-insurance">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 rounded-xl bg-emerald-600 text-white flex items-center justify-center shadow-sm shadow-emerald-600/30">
            <Shield className="h-4.5 w-4.5" />
          </div>
          <div>
            <div className="font-['Outfit'] font-bold text-slate-900 text-sm">Accident Cover</div>
            <div className="text-[11px] text-slate-500">Active while you're on pickup</div>
          </div>
        </div>
        <div className="bg-white/70 rounded-xl border border-emerald-100 p-4 text-center mb-3" data-testid="insurance-cover-amount">
          <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-600">Total Cover</div>
          <div className="font-['Outfit'] text-3xl font-extrabold text-slate-900 mt-0.5 flex items-center justify-center">
            <IndianRupee className="h-6 w-6" />
            {((ins.cover_inr || 0) / 100000).toFixed(1)}L
          </div>
          <div className="text-[10px] text-slate-500 mt-1">{ins.partner_note || "Coverage details inside your app"}</div>
        </div>
        <div className="grid grid-cols-2 gap-2 text-[10px]">
          <div className="bg-white/60 rounded-lg px-2 py-1.5 border border-emerald-100">
            <div className="font-bold text-slate-700">✔ Road accident</div>
            <div className="text-slate-500">medical + hospitalisation</div>
          </div>
          <div className="bg-white/60 rounded-lg px-2 py-1.5 border border-emerald-100">
            <div className="font-bold text-slate-700">✔ Death / disability</div>
            <div className="text-slate-500">lump-sum to family</div>
          </div>
        </div>
      </div>

      {/* JOINING BONUS */}
      <div className={`rounded-2xl p-5 border ${
        bonus.already_awarded ? "bg-gradient-to-br from-yellow-50 via-amber-50/60 to-white border-yellow-200/80" : "bg-gradient-to-br from-violet-50 via-indigo-50/60 to-white border-violet-200/80"
      }`} data-testid="perks-joining-bonus">
        <div className="flex items-center gap-2 mb-3">
          <div className={`w-9 h-9 rounded-xl text-white flex items-center justify-center shadow-sm ${bonus.already_awarded ? "bg-yellow-500" : "bg-violet-600"}`}>
            <Trophy className="h-4.5 w-4.5" />
          </div>
          <div className="flex-1">
            <div className="font-['Outfit'] font-bold text-slate-900 text-sm">Joining Bonus</div>
            <div className="text-[11px] text-slate-500 line-clamp-1">{bonus.campaign_name || "Launch Bonus"}</div>
          </div>
          {bonus.already_awarded && (
            <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-yellow-700 bg-yellow-50 border border-yellow-300 px-1.5 py-0.5 rounded" data-testid="bonus-awarded-badge">
              <CheckCircle2 className="h-2.5 w-2.5" /> EARNED
            </span>
          )}
        </div>

        <div className="bg-white/70 rounded-xl border border-slate-100 p-4 text-center mb-3">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Reward</div>
          <div className="font-['Outfit'] text-3xl font-extrabold text-slate-900 mt-0.5 flex items-center justify-center">
            <IndianRupee className="h-6 w-6" />
            {bonus.already_awarded ? (bonus.award_amount || bonus.bonus_amount) : bonus.bonus_amount || 0}
          </div>
          <div className="text-[10px] text-slate-500 mt-1">
            Finish <span className="font-bold text-slate-700">{bonus.required_pickups || 20}</span> pickups · first <span className="font-bold text-slate-700">{bonus.first_n_agents || 15}</span> agents only
          </div>
        </div>

        {!bonus.already_awarded && (
          <>
            <div className="flex items-center justify-between text-[10px] font-semibold text-slate-600 mb-1">
              <span className="inline-flex items-center gap-1"><Clock className="h-2.5 w-2.5" /> Your progress</span>
              <span data-testid="bonus-progress-text">{bonus.pickups_completed || 0} / {bonus.required_pickups || 20}</span>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden" data-testid="bonus-progress-bar">
              <div className="h-full bg-violet-500 transition-all" style={{ width: `${progressPct}%` }} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
